import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, BarChart3 } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

import { reportApi } from "@/lib/extensions-api";
import { API_BASE_URL } from "@/lib/constants";
import { formatCurrency } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import type { ReportPeriod } from "@/types/report";

export function SalesReportPage() {
  const [period, setPeriod] = useState<ReportPeriod>("monthly");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-sales-report", period, from, to],
    queryFn: () => reportApi.sales({
      period,
      date_from: period === "custom" ? from : undefined,
      date_to:   period === "custom" ? to   : undefined,
    }),
    enabled: period !== "custom" || (!!from && !!to),
  });

  const chartData = (data?.breakdown ?? []).map((r) => ({
    date: r.date.slice(5),
    total: Number(r.total_sales),
    count: r.transaction_count,
  }));

  const pdfUrl = `${API_BASE_URL}/admin/reports/export/pdf?type=sales&period=${period}${
    period === "custom" ? `&date_from=${from}&date_to=${to}` : ""
  }`;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Sales Report</h1>
          <p className="text-sm text-surface-500 mt-1">
            Aggregated sales for completed orders, grouped by day.
          </p>
        </div>
        <a href={pdfUrl} target="_blank" rel="noreferrer">
          <Button leftIcon={<Download className="h-4 w-4" />}>Download PDF</Button>
        </a>
      </header>

      <Card>
        <CardBody className="flex flex-wrap gap-3 items-end">
          <div className="w-48">
            <Select label="Period"
                     value={period}
                     onChange={(e) => setPeriod(e.target.value as ReportPeriod)}
                     options={[
                       { value: "today",   label: "Today" },
                       { value: "weekly",  label: "This Week" },
                       { value: "monthly", label: "This Month" },
                       { value: "yearly",  label: "This Year" },
                       { value: "custom",  label: "Custom Range" },
                     ]} />
          </div>
          {period === "custom" && (
            <div>
              <div className="text-sm font-medium text-surface-800 mb-1.5">Date Range</div>
              <DateRangePicker from={from} to={to}
                                 onChange={(f, t) => { setFrom(f); setTo(t); }} />
            </div>
          )}
        </CardBody>
      </Card>

      {isLoading ? <Skeleton className="h-80 rounded-xl" /> :
       data && data.breakdown.length > 0 ? (
        <>
          <Card>
            <CardHeader><CardTitle>Trend</CardTitle></CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip contentStyle={{
                    borderRadius: 8, border: "1px solid #cbd5e1",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)", fontSize: 12,
                  }} />
                  <Area type="monotone" dataKey="total"
                        stroke="#2563eb" strokeWidth={2}
                        fill="url(#salesGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>

          <Card>
            <CardHeader><CardTitle>Daily Breakdown</CardTitle></CardHeader>
            <CardBody className="p-0">
              <Table>
                <THead>
                  <TR>
                    <TH>Date</TH>
                    <TH className="text-right">Transactions</TH>
                    <TH className="text-right">Total Sales</TH>
                  </TR>
                </THead>
                <tbody>
                  {data.breakdown.map((r) => (
                    <TR key={r.date}>
                      <TD>{r.date}</TD>
                      <TD className="text-right">{r.transaction_count}</TD>
                      <TD className="text-right font-semibold">{formatCurrency(r.total_sales)}</TD>
                    </TR>
                  ))}
                  <TR className="bg-surface-50 font-bold">
                    <TD>Total</TD>
                    <TD className="text-right">{data.summary.transaction_count}</TD>
                    <TD className="text-right text-brand-700">{formatCurrency(data.summary.total_sales)}</TD>
                  </TR>
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </>
      ) : (
        <EmptyState icon={BarChart3} title="No data for this period" />
      )}
    </div>
  );
}
